import React, { useState } from 'react';
import {
  Home,
  Globe,
  List,
  LayoutDashboard,
  MapPin,
  Network,
  Shield,
  Sparkles,
  Settings,
  Users,
  ChevronLeft,
  ChevronRight,
  PanelLeftClose,
  PanelLeftOpen,
  Loader2,
  ClipboardList,
  Building,
} from 'lucide-react';
import PlatformSwitcher from './PlatformSwitcher';
import { firstName as userFirstName } from '../utils/userName';

/**
 * NavRail — Atlassian-style collapsible left navigation rail.
 *
 * Collapsed: 56px wide, icons only with title tooltips.
 * Expanded: 240px wide, icons + labels.
 * Persists state to localStorage under key `nav_rail_expanded`.
 *
 * MOBILE: hidden on < md breakpoint (mobile uses BottomNav).
 * DESKTOP: sits far left, full height, stacked vertically.
 *
 * Props:
 *   activeView       — 'map' | 'list' | 'dashboard' | 'visits' | 'platform'
 *   onViewChange     — (view: string) => void  — drives currentView in App.js
 *   currentUser      — { first_name, last_name, role, plan, ... }
 *   onLogout         — () => void
 *   onOpenSettings   — () => void   (opens MySettings)
 *   onOpenProfile    — () => void   (opens ProfileView)
 *   onGetBriefing    — () => void   (triggers AI briefing)
 *   visitCount       — number (0+)  — badge on Visits item
 *   workCount        — number (0+)  — open tasks + pending handovers (Tasks badge)
 *   isBriefingLoading — boolean      — spinner on AI Briefing item
 *   isBriefingOpen    — boolean      — dot indicator when briefing is active
 */
function NavRail({
  activeView,
  onViewChange,
  currentUser,
  onLogout,
  onOpenSettings,
  onOpenTeamSettings,
  onOpenProfile,
  onGetBriefing,
  visitCount = 0,
  workCount = 0,
  isBriefingLoading = false,
  isBriefingOpen = false,
}) {
  // Three-state rail: 'expanded' (240px) → 'icons' (56px) → 'hidden' (0px)
  // Persisted to localStorage under 'nav_rail_state'
  const [railState, setRailState] = useState(() => {
    try {
      const stored = localStorage.getItem('nav_rail_state');
      if (stored === 'icons' || stored === 'hidden') return stored;
      // Legacy key fallback
      const legacy = localStorage.getItem('nav_rail_expanded');
      return legacy === 'false' ? 'icons' : 'expanded';
    } catch {
      return 'expanded';
    }
  });

  const expanded = railState === 'expanded';
  const isHidden = railState === 'hidden';

  // Main toggle is bidirectional: expanded ↔ icons.
  // This guarantees the user can always get back to "expanded" from "icons"
  // (the previous forward-only cycle made that impossible).
  const toggleCollapse = () => {
    setRailState(prev => {
      const next = prev === 'expanded' ? 'icons' : 'expanded';
      try { localStorage.setItem('nav_rail_state', next); } catch {}
      return next;
    });
  };

  // "Hide entirely" is a separate intent — triggered by a dedicated button.
  const hideRail = () => {
    setRailState('hidden');
    try { localStorage.setItem('nav_rail_state', 'hidden'); } catch {}
  };

  // From the hidden-state floating edge-button: restore to expanded
  // (previously restored only to icons, which compounded the uncollapse bug).
  const showFromHidden = () => {
    setRailState('expanded');
    try { localStorage.setItem('nav_rail_state', 'expanded'); } catch {}
  };

  // Derive user initials and display name for the avatar.
  // Fall back to email if first/last name are unset (common for admin
  // seed accounts like hello@northai.app), and only show '?' as a
  // last resort when even the email is missing.
  const firstName = currentUser?.first_name || '';
  const email = currentUser?.email || '';
  const initial =
    (firstName && firstName[0].toUpperCase()) ||
    (email && email[0].toUpperCase()) ||
    '?';
  // First name only, like every other surface — see utils/userName.
  const displayName = userFirstName(currentUser, 'User');
  const role = currentUser?.role || '';
  const isAdmin = role === 'admin';

  // When fully hidden, render a fixed floating tab anchored to the left edge
  if (isHidden) {
    return (
      <div className="hidden md:block">
        <button
          onClick={showFromHidden}
          title="Show sidebar"
          aria-label="Show sidebar"
          className="fixed left-0 top-1/2 -translate-y-1/2 z-50
            bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-gray-50
            rounded-r-lg px-1.5 py-4 shadow-lg border border-l-0 border-gray-700
            transition-colors duration-150 flex items-center"
        >
          <PanelLeftOpen size={16} />
        </button>
      </div>
    );
  }

  // Auto-expand when the platform switcher opens so its dropdown is never clipped
  const handlePlatformSwitcherOpen = () => {
    if (railState !== 'expanded') {
      setRailState('expanded');
      try { localStorage.setItem('nav_rail_state', 'expanded'); } catch {}
    }
  };

  return (
    <nav
      className={`
        hidden md:flex flex-col bg-gray-900 h-screen flex-shrink-0
        transition-all duration-300 ease-in-out relative z-[51]
        ${expanded ? 'w-60' : 'w-14'}
      `}
      aria-label="Main navigation"
    >
      {/* Subtle top-accent gradient overlay */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-b from-blue-900/30 to-transparent pointer-events-none" />

      {/* ── Header: waffle + brand ────────────────────────────── */}
      <div className="flex items-center gap-3 px-3 py-4 border-b border-gray-800 min-h-[60px] flex-shrink-0">
        {expanded ? (
          <>
            <PlatformSwitcher
              currentUser={currentUser}
              activeProduct="geoagent"
              onOpen={handlePlatformSwitcherOpen}
            />
            <span className="text-blue-400 font-semibold text-sm tracking-wide truncate">
              NorthAI
            </span>
          </>
        ) : (
          /* Icon-only: clicking auto-expands then opens the dropdown */
          <PlatformSwitcher
            currentUser={currentUser}
            activeProduct="geoagent"
            iconOnly
            onOpen={handlePlatformSwitcherOpen}
          />
        )}
      </div>

      {/* ── Primary nav items ──────────────────────────────────── */}
      <div className="flex flex-col gap-1 px-2 pt-3 flex-1 overflow-y-auto overflow-x-hidden min-w-0">
        {/* Admin Hub first — for admins, setup & operations (data import,
            org settings, services) outrank the field-sales views below. */}
        {isAdmin && (
          <>
            <NavItem
              icon={Shield}
              label="Admin Hub"
              active={activeView === 'admin'}
              onClick={() => onViewChange('admin')}
              collapsed={!expanded}
              testId="nav-admin-hub"
            />
            <div className="border-t border-gray-800 my-1" aria-hidden="true" />
          </>
        )}
        <NavItem
          icon={Home}
          label="Home"
          active={activeView === 'platform'}
          onClick={() => onViewChange('platform')}
          collapsed={!expanded}
          testId="nav-home"
        />
        <NavItem
          icon={Globe}
          label="Map"
          active={activeView === 'map'}
          onClick={() => onViewChange('map')}
          collapsed={!expanded}
          testId="nav-map"
        />
        <NavItem
          icon={List}
          label="List"
          active={activeView === 'list'}
          onClick={() => onViewChange('list')}
          collapsed={!expanded}
          testId="nav-list"
        />
        <NavItem
          icon={LayoutDashboard}
          label="Dashboard"
          active={activeView === 'dashboard'}
          onClick={() => onViewChange('dashboard')}
          collapsed={!expanded}
          testId="nav-dashboard"
        />
        <NavItem
          icon={Network}
          label="Graph"
          active={activeView === 'graph'}
          onClick={() => onViewChange('graph')}
          collapsed={!expanded}
          testId="nav-graph"
        />
        <NavItem
          icon={MapPin}
          label="Visits"
          active={activeView === 'visits'}
          onClick={() => onViewChange('visits')}
          collapsed={!expanded}
          badge={visitCount > 0 ? visitCount : null}
          testId="nav-visits"
        />
        {['key_account_manager', 'hq_manager', 'admin'].includes(role) && (
          <NavItem
            icon={Building}
            label="Customers"
            active={activeView === 'accounts'}
            onClick={() => onViewChange('accounts')}
            collapsed={!expanded}
            testId="nav-accounts"
          />
        )}
        <NavItem
          icon={ClipboardList}
          label="Tasks"
          active={activeView === 'tasks'}
          onClick={() => onViewChange('tasks')}
          collapsed={!expanded}
          badge={workCount > 0 ? workCount : null}
          testId="nav-tasks"
        />
        <NavItem
          icon={Sparkles}
          label="AI Briefing"
          active={isBriefingOpen}
          onClick={() => {
            if (onGetBriefing) onGetBriefing();
          }}
          collapsed={!expanded}
          loading={isBriefingLoading}
          indicator={isBriefingOpen && !isBriefingLoading}
          testId="nav-ai-briefing"
        />
      </div>

      {/* ── Bottom section: settings, avatar, collapse ─────────── */}
      <div className="flex flex-col gap-1 px-2 pb-2 border-t border-gray-800 pt-2">
        {/* Admin entries (Import Data, Org Settings, Org Services) moved
            into the Admin Hub view — see the top of the primary nav list. */}
        {/* Team Settings — managers only (sets per-salesperson visit permissions).
            Admins have it as a tab in the Admin Hub, with the other org settings. */}
        {['area_manager', 'hq_manager'].includes(role) && onOpenTeamSettings && (
          <NavItem
            icon={Users}
            label="Team Settings"
            active={activeView === 'team-settings'}
            onClick={onOpenTeamSettings}
            collapsed={!expanded}
            testId="nav-team-settings"
          />
        )}
        <NavItem
          icon={Settings}
          label="My Settings"
          active={activeView === 'my-settings'}
          onClick={onOpenSettings}
          collapsed={!expanded}
          testId="nav-my-settings"
        />

        {/* User avatar button */}
        <button
          onClick={onOpenProfile}
          title={!expanded ? displayName : undefined}
          data-testid="nav-user-avatar"
          className="w-full flex items-center gap-3 px-2 py-2 rounded-lg text-sm font-medium
                     text-gray-400 hover:bg-gray-800 hover:text-gray-50 transition-colors duration-150"
        >
          {/* Avatar circle */}
          <span
            className="flex-shrink-0 w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-600
                       flex items-center justify-center text-white text-xs font-bold"
            aria-hidden="true"
          >
            {initial}
          </span>

          {expanded && (
            <span className="flex flex-col items-start min-w-0">
              <span className="text-gray-50 text-xs font-medium truncate max-w-[140px]">
                {displayName}
              </span>
              <span className="text-gray-500 text-xs truncate max-w-[140px] capitalize">
                {role.replace('_', ' ')}
              </span>
            </span>
          )}
        </button>

        {/* Collapse / expand toggle — bidirectional expanded ↔ icons */}
        <button
          onClick={toggleCollapse}
          title={expanded ? 'Collapse to icons' : 'Expand sidebar'}
          data-testid="nav-collapse-toggle"
          className="w-full flex items-center justify-center py-2 text-gray-500 hover:text-gray-50
                     rounded-lg hover:bg-gray-800 transition-colors duration-150"
          aria-label={expanded ? 'Collapse to icons' : 'Expand sidebar'}
        >
          {expanded ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
        </button>

        {/* Hide entirely — separate intent from collapse */}
        <button
          onClick={hideRail}
          title="Hide sidebar"
          aria-label="Hide sidebar"
          data-testid="nav-hide-rail"
          className="w-full flex items-center justify-center py-1.5 text-gray-600 hover:text-gray-50
                     rounded-lg hover:bg-gray-800 transition-colors duration-150"
        >
          <PanelLeftClose size={14} />
        </button>
      </div>
    </nav>
  );
}

/**
 * NavItem — a single icon+label row inside the rail.
 *
 * When collapsed, only the icon renders; the `title` attr provides a
 * native browser tooltip so the label is still discoverable.
 *
 * Optional props:
 *   badge     — number | null: count pill shown right of label, or red dot when collapsed
 *   loading   — boolean: replaces icon with spinner
 *   indicator — boolean: small pulsing dot overlay (active/non-loading state)
 */
function NavItem({ icon: Icon, label, active, onClick, collapsed, badge = null, loading = false, indicator = false, testId = undefined }) {
  const hasBadge = badge !== null && badge !== undefined && badge > 0;
  return (
    <button
      onClick={onClick}
      title={collapsed ? (hasBadge ? `${label} (${badge})` : label) : undefined}
      data-testid={testId}
      className={`
        w-full flex items-center gap-3 px-2 py-2.5 rounded-lg text-sm font-medium
        transition-colors duration-150 group relative
        ${active
          ? 'bg-blue-600 text-white shadow-sm shadow-blue-800'
          : 'text-gray-400 hover:bg-gray-800 hover:text-gray-50'
        }
      `}
      aria-current={active ? 'page' : undefined}
    >
      <span className="relative shrink-0 flex items-center justify-center">
        {loading ? (
          <Loader2 size={18} className="animate-spin" />
        ) : (
          <Icon size={18} />
        )}
        {/* Collapsed-state affordances: dot for badge, pulsing dot for active indicator */}
        {collapsed && hasBadge && !loading && (
          <span
            className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-purple-400 ring-2 ring-gray-900"
            aria-hidden="true"
          />
        )}
        {collapsed && indicator && !loading && !hasBadge && (
          <span
            className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-blue-400 ring-2 ring-gray-900 animate-pulse"
            aria-hidden="true"
          />
        )}
      </span>
      {!collapsed && (
        <>
          <span className="truncate flex-1 text-left">{label}</span>
          {hasBadge && !loading && (
            <span
              className={`
                ml-auto flex-shrink-0 px-1.5 py-0.5 rounded-full text-xs font-bold leading-none
                ${active
                  ? 'bg-white/20 text-white'
                  : 'bg-purple-900/60 text-purple-200 group-hover:bg-purple-800/70'}
              `}
            >
              {badge > 99 ? '99+' : badge}
            </span>
          )}
          {indicator && !loading && !hasBadge && (
            <span
              className="ml-auto flex-shrink-0 w-2 h-2 rounded-full bg-blue-400 animate-pulse"
              aria-hidden="true"
            />
          )}
        </>
      )}
    </button>
  );
}

export default NavRail;
